import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { API, graphqlOperation } from 'aws-amplify';
import {
  getIOTDocument,
  onUpdateIOTDocument,
} from '../../graphql/iot-documents';
import { Breadcumb, LineChart, EnergyHistoryChart } from '../../components';
import {
  getStatus,
  createReconnectingSubscription,
  GetColor,
} from '../../utils';
import {
  Velocimetro,
  InformacoesAplicacao,
  ConfigAplicacao,
  MachineComparison,
  PlantAvailability,
  PlantFinance,
  PlantParams,
  MachineAnomalies,
  MachineLog,
  MachineChangeLog,
  BlueboxFftChart,
} from './content';
import { isBlueboxThings } from '../../constants/bluebox';
import * as s from './styled-appliance';

// Code splitting do modal de analise avancada — todo o bundle
// (ChartStatsPanel + ChartHistogram + VariableMultiPicker +
// chart-stats + chart-export + styled-advanced-modal) so e baixado
// quando o operador clica em "Avancado". Pra maioria das sessoes de
// monitoramento, o operador olha so o resumo basico — esses ~20kb
// economizam no first paint.
const AdvancedModal = lazy(() => import('./advanced-modal/advanced-modal'));

const Produto = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { customer } = useSelector((state) => state.usuario);
  // Role pra gatear o cadastro de parametros analiticos (tarifa/placa).
  const role = useSelector((state) =>
    ((state.usuario.user && state.usuario.user.role) || '')
      .toString()
      .trim()
      .toLowerCase()
  );
  const canEditParams = role === 'higra' || role === 'admin';
  // Modal de parametros + chave de refresh dos KPIs apos salvar.
  const [paramsOpen, setParamsOpen] = useState(false);
  const [paramsRefreshKey, setParamsRefreshKey] = useState(0);
  // Guard de refresh direto sem state de navegacao: a rota nao tem :id
  // na URL, entao se o redux-persist nao rehydratou ou o usuario abriu a
  // URL crua (aba nova, link compartilhado), nao temos como saber qual
  // produto carregar. Redirect pro /dashboard/customer em vez de crashar
  // tentando acessar produto.thing de undefined.
  const produto = history.location.state;
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('daily');
  const [selectedThing, setSelectedThing] = useState({});
  const [selectedVar, setSelectedVar] = useState('');
  const [conexoes, setConexoes] = useState([]);
  // Estado do modal de analise avancada. Abre com botao 'avancado' no
  // LineChart embarcado. Toda a logica do modal (compareVars, view
  // toggle, ESC, fetch das compareVars, export CSV/PNG, stats) foi
  // movida pro AdvancedModal — esse arquivo so segura o boolean e
  // monta o modal lazy.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Aba ativa dos blocos analiticos (Operacao / Analise / Financeiro /
  // Por maquina). Agrupa as sections que antes empilhavam num scroll longo.
  const [analyticsTab, setAnalyticsTab] = useState('operacao');
  const [historyTab, setHistoryTab] = useState('historico');
  // #5 Correlacao Log<->grafico: timestamp (epoch ms) do evento clicado no
  // Log. Passado ao LineChart pra desenhar o marcador vertical. chartRef
  // rola o grafico pra viewport quando as colunas empilham (telas estreitas).
  const [focusEventTs, setFocusEventTs] = useState(null);
  const chartColumnRef = React.useRef(null);

  const handleLogEventClick = React.useCallback((evt) => {
    if (!evt || !Number.isFinite(evt.ts)) return;
    setFocusEventTs(evt.ts);
    // scrollIntoView so quando o grafico nao esta visivel (colunas
    // empilhadas). Em layout lado-a-lado e no-op visual.
    if (chartColumnRef.current && chartColumnRef.current.scrollIntoView) {
      chartColumnRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, []);

  useEffect(() => {
    if (history.location.state) {
      setSelectedThing({ ...history.location.state.thing[0], position: 0 });
    }
    return () =>
      dispatch({
        type: 'HISTORIC/RESET',
      });
  }, [history.location.state, dispatch]);

  useEffect(() => {
    if (!produto || !produto.thing || !produto.thing.length) return undefined;

    // Fecha conexoes anteriores (caso o effect re-execute)
    conexoes.forEach((unsubscribe) => {
      if (unsubscribe) unsubscribe();
    });

    const recons = [];
    let mounted = true;
    let blueboxPollInterval = null;
    const shouldPollBluebox = isBlueboxThings(produto.thing);

    const publishIotData = (th, data) => {
      if (!data || !mounted) return;
      dispatch({
        type: 'APLICACAO/SET_REALTIME_DATA',
        data: {
          type: '1',
          onTime: data.PotenciaAtivaMedia,
        },
        thing: th.thing,
      });
      dispatch({
        type: 'APLICACAO/SET_VELOCIMETRO_DATA',
        data: { [th.id]: data },
      });
    };

    const pollBluebox = async (iotQuery) => {
      await Promise.all(
        produto.thing.map(async (th) => {
          try {
            const response = await API.graphql(
              graphqlOperation(iotQuery, { thingName: th.thing })
            );
            publishIotData(th, response.getIOT || response.data?.getIOT);
          } catch (_) {
            // A subscription segue ativa; o proximo ciclo tenta novamente.
          }
        })
      );
    };

    Promise.all([getIOTDocument(), onUpdateIOTDocument()]).then(
      ([iotQuery, updateIotSubscription]) => {
        if (!mounted) return;
        produto.thing.forEach((th) => {
          const recon = createReconnectingSubscription({
            label: `appliance/${th.thing}`,
            buildSubscription: ({ next, error }) =>
              API.graphql(
                graphqlOperation(updateIotSubscription, { thingName: th.thing })
              ).subscribe({ next, error }),
            onNext: ({ value }) => publishIotData(th, value?.data?.onUpdateIOT),
          });
          recons.push(recon);
        });

        pollBluebox(iotQuery);
        if (shouldPollBluebox) {
          blueboxPollInterval = setInterval(() => pollBluebox(iotQuery), 3000);
        }
      }
    );

    setConexoes(recons.map((r) => () => r.unsubscribe()));

    // Cleanup: unsubscribe de todos os recons ao desmontar / mudar things
    return () => {
      mounted = false;
      if (blueboxPollInterval) clearInterval(blueboxPollInterval);
      recons.forEach((r) => {
        if (r && typeof r.unsubscribe === 'function') r.unsubscribe();
      });
    };
    // O effect deve disparar APENAS quando produto.thing muda; adicionar `conexoes`
    // criaria loop (setConexoes dentro do effect dispararia re-execução).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produto && produto.thing, dispatch]);

  // Apos todos os hooks, redirecionamos se nao temos contexto de
  // navegacao. JSX abaixo acessa produto.id, produto.thing, etc — sem
  // este guard estourava TypeError em refresh sem state.
  if (!produto) {
    return <Redirect to="/dashboard/customer" />;
  }

  const renderBreadcumb = () => (
    <Breadcumb
      paths={
        customer
          ? [
              {
                pathName: 'Parque',
                to: '/dashboard/customer',
                action: () =>
                  dispatch({
                    type: 'APLICACAO/RESET',
                  }),
              },
              {
                pathName: selectedThing.type
                  ? selectedThing.type.id === '1' || selectedThing.type.id === 1
                    ? 'Ucha'
                    : 'Eba'
                  : 'Usina',
                to: '/dashboard/customer/plant',
              },
            ]
          : [
              {
                pathName: 'Dashboard',
                to: '/dashboard',
                action: () =>
                  dispatch({
                    type: 'APLICACAO/RESET',
                  }),
              },
              {
                pathName: 'Parque',
                to: '/dashboard/customer',
                action: () =>
                  dispatch({
                    type: 'APLICACAO/RESET',
                  }),
              },
              {
                pathName: selectedThing.type
                  ? selectedThing.type.id === '1' || selectedThing.type.id === 1
                    ? 'Ucha'
                    : 'Eba'
                  : 'Usina',
                to: '/dashboard/customer/plant',
              },
            ]
      }
      props={{ ...produto, id: Number(produto.id) }}
    />
  );

  // Derivacoes pro novo header: nome do produto, tipo (ucha/eba) pro
  // accent (orange/blue) do pin, label tipo (UCHA/EBA) prefixando o nome
  // ("UCHA — SABESP Campo Belo" estilo do design).
  const tipoKey = produto.type && produto.type.type; // 'ucha' | 'eba'
  const tipoUpper =
    tipoKey === 'ucha' ? 'UCHA' : tipoKey === 'eba' ? 'EBA' : 'USINA';
  // Contagem de maquinas a partir do array real (produto.thing) — mesma
  // fonte do "Maquina X de N" do detalhe operacional. Evita o
  // energy.thingsAmount do socket que chegava inconsistente (piscava
  // 1 -> 4). Exibido como chip ao lado do tag "Resumo".
  const machineCount = (produto.thing || []).length;
  const isBluebox = isBlueboxThings(produto.thing || []);
  // Abas dos blocos analiticos (agrupadas por tema). "Por maquina" so
  // aparece quando ha 2+ maquinas (comparativo nao faz sentido com 1).
  const analyticsTabList = [
    { key: 'operacao', label: 'Operação' },
    ...(machineCount > 1 ? [{ key: 'pormaquina', label: 'Por máquina' }] : []),
    ...(!isBluebox
      ? [{ key: 'disponibilidade', label: 'Disponibilidade' }]
      : []),
    { key: 'analise', label: 'Análise' },
    ...(!isBluebox ? [{ key: 'financeiro', label: 'Financeiro' }] : []),
    { key: 'log', label: 'Logs' },
    ...(!isBluebox ? [{ key: 'changeLog', label: 'Logs de alteração' }] : []),
  ];
  // Backend as vezes prefixa a description com o proprio tipo
  // ("UCHA - GENESIS / SILEA"), o que duplicava no header
  // ("UCHA — UCHA - GENESIS / SILEA"). Remove o prefixo redundante so
  // quando seguido de separador (-, –, —, /) pra nao cortar nomes que
  // por acaso comecem com as letras do tipo (ex.: "UCHApchSaoFrancisco").
  const cleanDescription = (produto.description || '')
    .replace(new RegExp(`^\\s*${tipoUpper}\\s*[-–—/]\\s*`, 'i'), '')
    .trim();
  const productLabel = cleanDescription
    ? `${tipoUpper} — ${cleanDescription}`
    : tipoUpper;
  const statusLabel = getStatus(produto.status);
  const statusColor = GetColor(statusLabel);

  return (
    <s.NewContainer>
      <s.HeaderBar>
        <s.HeaderLeft>
          <s.HeaderBreadcrumb>{renderBreadcumb()}</s.HeaderBreadcrumb>
          <s.HeaderName>
            <s.HeaderPin $accent={tipoKey}>
              {/* lucide:map-pin */}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </s.HeaderPin>
            <span>{productLabel}</span>
          </s.HeaderName>
        </s.HeaderLeft>
        <s.HeaderRight>
          {canEditParams && (
            <s.ParamsButton
              type="button"
              onClick={() => setParamsOpen(true)}
              title="Parâmetros analíticos (tarifa / potência nominal)"
            >
              {/* lucide:sliders-horizontal */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="21" y1="4" x2="14" y2="4" />
                <line x1="10" y1="4" x2="3" y2="4" />
                <line x1="21" y1="12" x2="12" y2="12" />
                <line x1="8" y1="12" x2="3" y2="12" />
                <line x1="21" y1="20" x2="16" y2="20" />
                <line x1="12" y1="20" x2="3" y2="20" />
                <line x1="14" y1="2" x2="14" y2="6" />
                <line x1="8" y1="10" x2="8" y2="14" />
                <line x1="16" y1="18" x2="16" y2="22" />
              </svg>
              <span>Parâmetros</span>
            </s.ParamsButton>
          )}
          {statusLabel && (
            <s.StatusChip $color={statusColor} title={`Status: ${statusLabel}`}>
              <s.StatusDot $color={statusColor} aria-hidden="true" />
              {statusLabel}
            </s.StatusChip>
          )}
        </s.HeaderRight>
      </s.HeaderBar>

      <s.NewContainerBody>
        <s.LeftColumnGrid>
          <s.Section>
            <s.SectionHead>
              <s.SectionTag $accent={tipoKey}>Resumo</s.SectionTag>
              {machineCount > 0 && (
                <s.SectionTag $accent={tipoKey}>
                  {machineCount} {machineCount === 1 ? 'máquina' : 'máquinas'}
                </s.SectionTag>
              )}
            </s.SectionHead>
            <s.SectionContent>
              <InformacoesAplicacao
                selectedProduto={produto}
                type={produto.type.type}
                things={produto.thing || []}
              />
            </s.SectionContent>
          </s.Section>

          {/* Abas dos blocos analiticos, agrupadas por tema pra evitar o
              scroll longo (o Resumo acima fica fixo):
                - Operacao:  detalhes operacionais (carrossel+diagrama+log)
                             + disponibilidade
                - Analise:   desvios do padrao + geracao/vazao diaria
                - Financeiro
                - Por maquina (so com 2+ maquinas) */}
          <s.AnalyticsTabs role="tablist">
            {analyticsTabList.map((t) => (
              <s.AnalyticsTab
                key={t.key}
                type="button"
                role="tab"
                aria-selected={analyticsTab === t.key}
                $active={analyticsTab === t.key}
                $accent={tipoKey}
                onClick={() => setAnalyticsTab(t.key)}
              >
                {t.label}
              </s.AnalyticsTab>
            ))}
          </s.AnalyticsTabs>

          {analyticsTab === 'operacao' && (
            /* Detalhes operacionais: carrossel + diagrama + cards + log
               (ConfigAplicacao tem as sub-abas Detalhes/Log). */
            <s.Section>
              <s.SectionContent>
                <ConfigAplicacao
                  aplicacao={produto}
                  things={produto.thing || []}
                  setSelectedThing={setSelectedThing}
                  selectedThing={selectedThing}
                  type={produto.type.type}
                />
              </s.SectionContent>
            </s.Section>
          )}

          {analyticsTab === 'disponibilidade' && !isBluebox && (
            /* Disponibilidade real + MTBF/MTTR no periodo (transicoes de
               status do backend). Aba propria. */
            <s.Section>
              <s.SectionHead>
                <s.SectionTag $accent={tipoKey}>Disponibilidade</s.SectionTag>
                <s.SectionHint>
                  Tempo em operação · MTBF / MTTR · falhas no período
                </s.SectionHint>
              </s.SectionHead>
              <s.SectionContent>
                <PlantAvailability
                  applianceId={produto.id}
                  accent={tipoKey}
                  refreshKey={paramsRefreshKey}
                />
              </s.SectionContent>
            </s.Section>
          )}

          {analyticsTab === 'analise' && (
            <>
              {/* Desvios do padrao (anomalias v1): valor atual vs baseline
                  robusto da MAQUINA SELECIONADA no carrossel. */}
              {selectedThing && selectedThing.id != null && (
                <s.Section>
                  <s.SectionHead>
                    <s.SectionTag $accent={tipoKey}>
                      Desvios do padrão
                    </s.SectionTag>
                    <s.SectionHint>
                      {selectedThing.thing || 'máquina selecionada'} · vs.
                      últimos 14 dias rodando
                    </s.SectionHint>
                  </s.SectionHead>
                  <s.SectionContent>
                    <MachineAnomalies thing={selectedThing} />
                  </s.SectionContent>
                </s.Section>
              )}

              {/* Historico diario dos ultimos 7 dias.
                  - UCHA: energia gerada (metric=energy, accent laranja)
                  - EBA:  vazao agregada das bombas (metric=flow, azul) */}
              {tipoKey === 'ucha' && (
                <s.Section>
                  <s.SectionHead>
                    <s.SectionTag $accent="ucha">
                      Geração de energia
                    </s.SectionTag>
                    <s.SectionHint>
                      Energia gerada — últimos 7 dias · comparativo diário
                    </s.SectionHint>
                  </s.SectionHead>
                  <s.SectionContent>
                    <EnergyHistoryChart
                      applianceId={produto.id}
                      type={produto.type.type}
                      days={7}
                      metric="energy"
                    />
                  </s.SectionContent>
                </s.Section>
              )}
              {tipoKey === 'eba' && (
                <s.Section>
                  <s.SectionHead>
                    <s.SectionTag $accent="eba">Vazão diária</s.SectionTag>
                    <s.SectionHint>
                      Vazão média — últimos 7 dias · comparativo diário
                    </s.SectionHint>
                  </s.SectionHead>
                  <s.SectionContent>
                    <EnergyHistoryChart
                      applianceId={produto.id}
                      type={produto.type.type}
                      days={7}
                      metric="flow"
                    />
                  </s.SectionContent>
                </s.Section>
              )}
            </>
          )}

          {analyticsTab === 'financeiro' && !isBluebox && (
            /* Financeiro: tarifa efetiva x energia do periodo = custo (EBA)
               / receita (UCHA). Mostra '—' ate haver tarifa cadastrada. */
            <s.Section>
              <s.SectionHead>
                <s.SectionTag $accent={tipoKey}>Financeiro</s.SectionTag>
                <s.SectionHint>
                  {tipoKey === 'ucha' ? 'Receita' : 'Custo'} estimado · tarifa ×
                  energia no período
                </s.SectionHint>
              </s.SectionHead>
              <s.SectionContent>
                <PlantFinance
                  applianceId={produto.id}
                  accent={tipoKey}
                  refreshKey={paramsRefreshKey}
                />
              </s.SectionContent>
            </s.Section>
          )}

          {analyticsTab === 'pormaquina' && machineCount > 1 && (
            /* Comparacao por maquina — barra proporcional da potencia
               atual de cada uma pra ver de relance quem mais contribui. */
            <s.Section>
              <s.SectionHead>
                <s.SectionTag $accent={tipoKey}>Por máquina</s.SectionTag>
                <s.SectionHint>
                  Potência atual por máquina · tempo real
                </s.SectionHint>
              </s.SectionHead>
              <s.SectionContent>
                <MachineComparison
                  things={produto.thing || []}
                  type={produto.type.type}
                />
              </s.SectionContent>
            </s.Section>
          )}

          {analyticsTab === 'log' && (
            /* Log: historico de alarmes/bloqueios/trips da maquina
               selecionada no carrossel. */
            <s.Section>
              <s.SectionHead>
                <s.SectionTag $accent={tipoKey}>Log</s.SectionTag>
                <s.SectionHint>
                  {selectedThing.thing || 'máquina selecionada'} · alarmes /
                  bloqueios / trips
                </s.SectionHint>
              </s.SectionHead>
              <s.SectionContent>
                <MachineLog
                  thingId={selectedThing.id}
                  onEventClick={handleLogEventClick}
                />
              </s.SectionContent>
            </s.Section>
          )}

          {analyticsTab === 'changeLog' && !isBluebox && (
            /* Logs de alteração: historico de alteracoes de setpoint,
               comando e range da maquina selecionada no carrossel. */
            <s.Section>
              <s.SectionHead>
                <s.SectionTag $accent={tipoKey}>Logs de alteração</s.SectionTag>
                <s.SectionHint>
                  {selectedThing.thing || 'máquina selecionada'} · setpoints /
                  comandos / ranges
                </s.SectionHint>
              </s.SectionHead>
              <s.SectionContent>
                <MachineChangeLog thing={selectedThing} parkId={produto.id} />
              </s.SectionContent>
            </s.Section>
          )}
        </s.LeftColumnGrid>

        <s.RightColumnGrid ref={chartColumnRef}>
          <Velocimetro
            things={produto.thing || []}
            aplicacao={produto}
            selectedPeriod={selectedPeriod}
            selectedVar={selectedVar}
            setSelectedVar={setSelectedVar}
            thing={selectedThing}
            setLoadingGraph={setLoadingGraph}
            loadingGraph={loadingGraph}
          />
          {isBluebox ? (
            <s.HistoryPanel>
              <s.HistoryTabs role="tablist" aria-label="Leituras históricas">
                <s.HistoryTab
                  type="button"
                  role="tab"
                  aria-selected={historyTab === 'historico'}
                  $active={historyTab === 'historico'}
                  onClick={() => setHistoryTab('historico')}
                >
                  Histórico
                </s.HistoryTab>
                {['X', 'Y', 'Z'].map((axis) => {
                  const tab = `fft${axis.toLowerCase()}`;
                  return (
                    <s.HistoryTab
                      key={axis}
                      type="button"
                      role="tab"
                      aria-selected={historyTab === tab}
                      $active={historyTab === tab}
                      onClick={() => setHistoryTab(tab)}
                    >
                      FFT{axis}
                    </s.HistoryTab>
                  );
                })}
              </s.HistoryTabs>
              {historyTab.startsWith('fft') ? (
                <BlueboxFftChart
                  thing={selectedThing}
                  axis={historyTab.slice(-1).toUpperCase()}
                />
              ) : (
                <LineChart
                  setLoadingGraph={(status) => setLoadingGraph(status)}
                  loadingGraph={loadingGraph}
                  thingId={selectedThing.id}
                  thing={selectedThing}
                  selectedVar={selectedVar}
                  setSelectedVar={setSelectedVar}
                  selectedPeriod={selectedPeriod}
                  setSelectedPeriod={setSelectedPeriod}
                  things={produto.thing || []}
                  mode="basic"
                  onRequestAdvanced={() => setAdvancedOpen(true)}
                  focusTs={focusEventTs}
                />
              )}
            </s.HistoryPanel>
          ) : (
            <LineChart
              setLoadingGraph={(status) => setLoadingGraph(status)}
              loadingGraph={loadingGraph}
              thingId={selectedThing.id}
              thing={selectedThing}
              selectedVar={selectedVar}
              setSelectedVar={setSelectedVar}
              selectedPeriod={selectedPeriod}
              setSelectedPeriod={setSelectedPeriod}
              things={produto.thing || []}
              mode="basic"
              onRequestAdvanced={() => setAdvancedOpen(true)}
              focusTs={focusEventTs}
            />
          )}
        </s.RightColumnGrid>
      </s.NewContainerBody>

      {/* Modal de analise avancada — code-splitted via React.lazy. So
          baixa o chunk (ChartStatsPanel + ChartHistogram + Picker +
          utils + styled) quando o operador clica em 'Avancado'. Suspense
          com fallback null pra evitar flash do modal antes do JS
          carregar. */}
      {advancedOpen && (
        <Suspense fallback={null}>
          <AdvancedModal
            isOpen={advancedOpen}
            onClose={() => setAdvancedOpen(false)}
            selectedThing={selectedThing}
            selectedVar={selectedVar}
            setSelectedVar={setSelectedVar}
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={setSelectedPeriod}
            things={produto.thing || []}
            loadingGraph={loadingGraph}
            setLoadingGraph={setLoadingGraph}
          />
        </Suspense>
      )}

      {/* Cadastro de parametros analiticos (tarifa da usina + placa por
          maquina) — so role privilegiado. Refresca os KPIs ao salvar. */}
      {canEditParams && (
        <PlantParams
          isOpen={paramsOpen}
          onClose={() => setParamsOpen(false)}
          applianceId={produto.id}
          things={produto.thing || []}
          onSaved={() => setParamsRefreshKey((k) => k + 1)}
        />
      )}
    </s.NewContainer>
  );
};

export default Produto;
